/**
 * ปลายทางของการแจ้งเตือน — ตารางเดียวใช้ร่วมกันทั้งกระดิ่ง (NotificationBell) และหน้า /notifications
 *
 * E2E UI-B finding B-01 (30 ก.ย. 2026): เดิมสองไฟล์ถือตารางคนละชุด และไม่มีคีย์ `quotation` /
 * `preparation_job` → แจ้งเตือน "ใบเสนอราคาจะหมดอายุ" กดแล้วไปแค่ /notifications และโมดัลรายละเอียด
 * เขียน "quotation ID: 284" (ชื่อ type อังกฤษ + id ภายใน)
 *
 * คีย์ = `reference_type` ที่ backend ส่งจริง:
 *   - `quotation`        ← backend/app/tasks/notification_tasks.py `_notify_quotation`
 *   - `preparation_job`  ← backend/app/services/preparation_notification.py `REFERENCE_TYPE`
 *
 * `permission` ต้องตรงกับ gate ของ route เป้าหมายใน App.tsx (G-16) — ผู้ใช้ไม่มีสิทธิ์ต้องไม่ถูกพาไปชน 403
 * `detail` = ปลายทางเป็นหน้าของรายการนั้นจริง (true) หรือแค่หน้ารายการของโมดูล (false · G-01: invoice/payment
 * ไม่มี route รายตัว) — โมดัลรายละเอียดทำลิงก์เฉพาะ detail=true เพราะป้ายลิงก์ระบุตัวรายการ
 */

import { deviceDetailUrl, simDetailUrl } from './deviceLinks';

export interface NotificationTarget {
  path: string;
  permission: string;
  detail: boolean;
}

type RefId = number | string;

interface RouteDef {
  path: (id: RefId) => string;
  permission: string;
  detail: boolean;
}

const REFERENCE_ROUTES: Record<string, RouteDef> = {
  maintenance: { path: (id) => `/maintenance/view/${id}`, permission: 'maintenance.view', detail: true },
  device_maintenance: { path: (id) => `/maintenance/view/${id}`, permission: 'maintenance.view', detail: true },
  rental: { path: (id) => `/rentals/view/${id}`, permission: 'rentals.view', detail: true },
  contract: { path: (id) => `/contracts/view/${id}`, permission: 'contracts.view', detail: true },
  invoice: { path: () => '/billing/invoices', permission: 'invoices.view', detail: false },
  payment: { path: () => '/billing/payments', permission: 'payments.view', detail: false },
  equipment_return: { path: () => '/rentals/equipment-returns', permission: 'equipment_returns.view', detail: false },
  equipment_return_follow_up: { path: () => '/rentals/equipment-returns', permission: 'equipment_returns.view', detail: false },
  // หน้าเต็มตั้งแต่ 28 ต.ค. 2026 (เดิม ?view= เปิดโมดัล · ลิงก์เก่ายังเด้งได้ด้วย LegacyDetailRedirect)
  device: { path: (id) => deviceDetailUrl(id), permission: 'devices.view', detail: true },
  // reference_id = sims.id (notification_tasks SIM_EXPIRING / crud.sim expire scan) → หน้ารายละเอียดซิม
  sim: { path: (id) => simDetailUrl(id), permission: 'sims.view', detail: true },
  quotation: { path: (id) => `/billing/quotations/${id}`, permission: 'quotations.view', detail: true },
  preparation_job: { path: (id) => `/rentals/preparation-jobs/${id}`, permission: 'preparation_jobs.view', detail: true },  // KD3 S2609 (FLOW-ISS-045 · BUG-S2609-001): สามชนิดที่ backend ยิงจริงแต่ไม่มีปลายทาง → กระดิ่ง/หน้ารวม
  // ตกไป fallback '/notifications' (dead-end) · reference_id = stock_issue.id / device.id / site.id
  feedback: { path: (id) => `/feedback/${id}`, permission: '', detail: true },
  stock_issue_overdue: { path: (id) => `/stock/pending-returns?issue=${id}`, permission: 'stock_issues.view', detail: true },
  device_low_stock: { path: (id) => deviceDetailUrl(id), permission: 'devices.view', detail: true },
  site_idle: { path: (id) => `/master-data/sites/${id}`, permission: 'sites.view', detail: true }, // หน้าเต็ม 28 ต.ค. 2026
};

/** reference_type ทุกตัวที่มีปลายทาง — ให้เทสต์ไล่ครบชุดได้ */
export const NOTIFICATION_REFERENCE_TYPES: ReadonlyArray<string> = Object.keys(REFERENCE_ROUTES);

const REFERENCE_LABELS: Record<string, string> = {
  maintenance: 'เคสซ่อมบำรุง',
  device_maintenance: 'เคสซ่อมบำรุง',
  rental: 'สัญญาเช่า',
  contract: 'สัญญา',
  invoice: 'ใบแจ้งหนี้',
  payment: 'การชำระเงิน',
  equipment_return: 'ใบตามเก็บอุปกรณ์',
  equipment_return_follow_up: 'ใบตามเก็บอุปกรณ์',
  device: 'อุปกรณ์',
  sim: 'ซิม',
  quotation: 'ใบเสนอราคา',
  preparation_job: 'ใบงานเตรียมเครื่อง',
  feedback: 'เรื่องที่แจ้ง',
  stock_issue_overdue: 'ใบเบิก-ยืมอุปกรณ์',
  device_low_stock: 'อุปกรณ์ใกล้หมดคลัง',
  site_idle: 'โครงการ',
};

/** คีย์ใน `notification.data` ที่เก็บเลขที่เอกสาร (ที่ผู้ใช้รู้จัก) ของแต่ละ reference_type */
const REFERENCE_NUMBER_KEYS: Record<string, string> = {
  quotation: 'quotation_number',
  feedback: 'report_number',
  preparation_job: 'job_number',
  stock_issue_overdue: 'issue_number',
};

/** ป้ายไทยของ `notification.type` ที่เพิ่มมากับโมดูลใบเสนอราคา/ใบงานเตรียมเครื่อง/สต็อก */
const TYPE_LABELS: Record<string, string> = {
  quotation_expiring: 'ใบเสนอราคาใกล้หมดอายุ',
  quotation_expired: 'ใบเสนอราคาหมดอายุแล้ว',
  preparation_job_new: 'ใบงานเตรียมเครื่องใหม่',
  preparation_job_assigned: 'มอบหมายใบงานเตรียมเครื่อง',
  preparation_job_ready: 'ใบงานพร้อมส่งมอบ',
  preparation_job_delivered: 'ใบงานส่งมอบแล้ว',
  feedback_new: 'มีเรื่องแจ้งปัญหาใหม่',
  feedback_status_changed: 'สถานะเรื่องที่แจ้งเปลี่ยน',
  feedback_comment: 'มีความคิดเห็นใหม่ในเรื่องที่แจ้ง',
  feedback_assigned: 'คุณได้รับมอบหมายเรื่องแจ้งปัญหา',
  low_stock: 'ของใกล้หมดคลัง',
  stock_issue_overdue: 'ใบเบิกเลยกำหนดคืน',
};

const has = (map: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(map, key);

/**
 * ปลายทางของ (reference_type, reference_id) · คืน null เมื่อไม่รู้จัก type หรือไม่มี id
 * (ผู้เรียกตัดสินเองว่าจะ fallback ไปไหน)
 */
export function resolveNotificationTarget(
  referenceType: string | null | undefined,
  referenceId: RefId | null | undefined
): NotificationTarget | null {
  if (!referenceType || referenceId === null || referenceId === undefined || referenceId === '') return null;
  if (!has(REFERENCE_ROUTES, referenceType)) return null;
  const def = REFERENCE_ROUTES[referenceType];
  return { path: def.path(referenceId), permission: def.permission, detail: def.detail };
}

/**
 * เปิดปลายทางได้ไหม — permission ว่าง = route ไม่ gate ด้วยสิทธิ์ (backend ตัดสินเอง เช่น feedback: ผู้แจ้งเปิดเรื่องตัวเองได้)
 * (E2E 27 ต.ค.: หน้า /notifications เรียก hasPermission('') ตรง ๆ = false → ผู้แจ้งไม่มีลิงก์ไปเรื่องของตัวเอง · กระดิ่งข้ามเช็คถูกแล้ว)
 */
export function canOpenNotificationTarget(
  target: NotificationTarget,
  hasPermission: (permission: string) => boolean
): boolean {
  return !target.permission || hasPermission(target.permission);
}

/** ป้ายไทยของ reference_type · ไม่รู้จัก = คืนค่าเดิม (ดีกว่าว่าง) */
export function notificationReferenceLabel(referenceType: string | null | undefined): string {
  if (!referenceType) return '';
  return has(REFERENCE_LABELS, referenceType) ? REFERENCE_LABELS[referenceType] : referenceType;
}

/**
 * ข้อความ "เกี่ยวข้องกับ" ในโมดัลรายละเอียด — ใช้เลขที่เอกสารจาก `data` เมื่อมี (QT-… / PRP-…)
 * ไม่มีจึงถอยไปใช้ id
 */
export function notificationReferenceText(
  referenceType: string | null | undefined,
  referenceId: RefId | null | undefined,
  data?: Record<string, unknown> | null
): string {
  const label = notificationReferenceLabel(referenceType);
  if (!label) return '';
  const numberKey = referenceType && has(REFERENCE_NUMBER_KEYS, referenceType) ? REFERENCE_NUMBER_KEYS[referenceType] : null;
  const docNumber = numberKey && data ? data[numberKey] : null;
  if (typeof docNumber === 'string' && docNumber.trim()) return `${label} ${docNumber.trim()}`;
  if (referenceId === null || referenceId === undefined || referenceId === '') return label;
  return `${label} ID: ${referenceId}`;
}

/** ป้ายไทยของ notification.type ชุดใหม่ · ไม่รู้จัก = null (ผู้เรียกใช้ fallback เดิมของตัวเอง) */
export function notificationTypeLabel(type: string | null | undefined): string | null {
  if (!type || !has(TYPE_LABELS, type)) return null;
  return TYPE_LABELS[type];
}
